import { useQuery } from "@tanstack/react-query";
import { Card, CardBody, Page } from "@theg1group/ui";
import { api } from "../lib/api.ts";
import { TimeframeSelect, useTimeframe } from "../lib/timeframe.tsx";

interface Overview {
	totals: { messages: number; domains: number; cases: number; ips: number };
	esp: Array<{ esp: string; count: number }>;
	asn: Array<{ asn: number; asn_org: string | null; count: number }>;
	tracking: Array<{ fqdn: string; count: number }>;
	freshDomains: { fresh: number; with_age: number };
}

export function Overview() {
	const { qs } = useTimeframe();
	const { data } = useQuery({
		queryKey: ["overview", qs],
		queryFn: () => api.get<Overview>(`/api/stats/overview?${qs}`),
	});

	const t = data?.totals;
	const fresh = data?.freshDomains;
	const freshPct =
		fresh && fresh.with_age > 0 ? Math.round((fresh.fresh / fresh.with_age) * 100) : 0;

	return (
		<Page
			title="Overview"
			description="Investigation KPIs across the selected timeframe."
			actions={<TimeframeSelect />}
		>
			<div className="grid gap-3.5 mb-3.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}>
				<Kpi label="Emails" value={t?.messages} />
				<Kpi label="Cases" value={t?.cases} />
				<Kpi label="Domains" value={t?.domains} />
				<Kpi label="IPs" value={t?.ips} />
				<Kpi label="Fresh domains" value={`${freshPct}%`} hint="registered <30d" />
			</div>

			<div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
				<Panel title="ESP distribution">
					{(data?.esp ?? []).map((e) => (
						<Row key={e.esp} label={e.esp} value={e.count} max={data?.esp[0]?.count ?? 1} />
					))}
				</Panel>
				<Panel title="Top hosting (ASN)">
					{(data?.asn ?? []).map((a) => (
						<Row
							key={a.asn}
							label={`AS${a.asn} ${a.asn_org ?? ""}`}
							value={a.count}
							max={data?.asn[0]?.count ?? 1}
						/>
					))}
				</Panel>
				<Panel title="Top tracking hosts">
					{(data?.tracking ?? []).map((h) => (
						<Row key={h.fqdn} label={h.fqdn} value={h.count} max={data?.tracking[0]?.count ?? 1} />
					))}
				</Panel>
			</div>
		</Page>
	);
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<Card>
			<CardBody>
				<h2 className="text-sm font-semibold text-content mb-3">{title}</h2>
				{children}
			</CardBody>
		</Card>
	);
}

function Kpi({ label, value, hint }: { label: string; value?: number | string; hint?: string }) {
	return (
		<Card>
			<CardBody>
				<div className="text-[11px] uppercase text-muted">{label}</div>
				<div className="text-2xl font-bold text-content">{value ?? "—"}</div>
				{hint && <div className="text-[11px] text-muted">{hint}</div>}
			</CardBody>
		</Card>
	);
}

function Row({ label, value, max }: { label: string; value: number; max: number }) {
	return (
		<div className="mb-1.5">
			<div className="flex justify-between text-xs text-content mb-0.5">
				<span className="truncate mr-2">{label}</span>
				<span className="text-muted">{value}</span>
			</div>
			<div className="h-1.5 rounded-pill bg-surface-2 overflow-hidden">
				<div className="h-full bg-primary" style={{ width: `${(value / max) * 100}%` }} />
			</div>
		</div>
	);
}
